import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnalyticEvents } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { TimeOffBackupApprover } from '@app/models/time-off-v3/time-off-backup-approver.model';
import { TimeOffBalanceDateSetting } from '@app/modules/time-off-v3/meta/time-off-meta';
import { TimeOffService } from '@app/modules/time-off-v3/services';
import { AnalyticService, AuthService, NotifyService, SettingsService } from '@app/services';

@Component({
    templateUrl: './general-settings-edit.template.html',
})
export class GeneralSettingsEditView implements OnInit {
    isLoading = false;

    // Default value for selected backup approver
    selectedBackupApprover = 0;
    activeAdmins: Employee[] = [];

    currentTimeOffBackupApprover = new TimeOffBackupApprover();
    toSaveTimeOffBackupApprover = new TimeOffBackupApprover();

    constructor(
        private auth: AuthService,
        private settingsService: SettingsService,
        private notify: NotifyService,
        private route: ActivatedRoute,
        private router: Router,
        private timeOffService: TimeOffService,
        private analyticService: AnalyticService
    ) {}
    ngOnInit(): void {
        this.getActiveAdmins();
        this.getDefaultApprover();
    }

    get timeOffVisibility(): boolean | undefined {
        return this.settingsService.getByName('timeOffRequests.visibility')?.value;
    }

    set timeOffVisibility(_: boolean) {
        this.settingsService
            .getByName('timeOffRequests.visibility')
            .param('company', this.auth.company.id)
            .toggle()
            .catch((err) => {
                err.message
                    ? this.notify.error(err.message)
                    : this.notify.error('time-off-v3.canNotToggleTimeOffVisibilitySetting');
            });
    }

    get timeOffBalanceDateToday(): boolean | undefined {
        return this.settingsService.getByName(TimeOffBalanceDateSetting)?.value;
    }

    set timeOffBalanceDateToday(_: boolean) {
        this.settingsService
            .getByName(TimeOffBalanceDateSetting)
            .param('company', this.auth.company.id)
            .toggle()
            .catch((err) => {
                err.message
                    ? this.notify.error(err.message)
                    : this.notify.error('time-off-v3.canNotUpdateTimeOffBalanceDateSetting');
            });
    }

    async onUpdateBackupApprover(id: number): Promise<void> {
        this.analyticService.trackEvent(AnalyticEvents.TimeOffSettingsOverallSettingsUpdateBackupApproverField);

        this.toSaveTimeOffBackupApprover = new TimeOffBackupApprover({ approverId: id });
    }

    async onSave(): Promise<void> {
        this.isLoading = true;

        // Only save when the user has changed there selection for a time off back approver.
        if (
            this.toSaveTimeOffBackupApprover.approverId === null ||
            this.toSaveTimeOffBackupApprover.approverId === undefined
        ) {
            this.notify.success('time-off-v3.generalSettingsSuccess');

            this.onRedirect();
            return;
        }

        try {
            if (this.currentTimeOffBackupApprover.id) {
                await this.currentTimeOffBackupApprover.delete();
                this.currentTimeOffBackupApprover = new TimeOffBackupApprover();
            }

            this.notify.success('time-off-v3.generalSettingsSuccess');

            this.onRedirect();

            if (!this.toSaveTimeOffBackupApprover.approverId) {
                return;
            }

            this.currentTimeOffBackupApprover = await this.toSaveTimeOffBackupApprover.save();

            this.notify.success('time-off-v3.generalSettingsSuccess');

            this.onRedirect();
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    onRedirect(): void {
        this.router.navigate(['time-off', 'v3', 'settings']);
    }

    private async getActiveAdmins(): Promise<void> {
        this.isLoading = true;

        try {
            const [activeAdmins] = await Employee.param('company', this.auth.company.id)
                .orderBy('firstName', 'ASC')
                .where('adminOnly', true)
                .all();
            this.activeAdmins = activeAdmins;
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    private async getDefaultApprover(): Promise<void> {
        this.isLoading = true;
        try {
            const timeOffBackupApprover = await this.timeOffService.getTimeOffBackupApprover();

            this.selectedBackupApprover = timeOffBackupApprover.approverId ?? 0;

            this.currentTimeOffBackupApprover = new TimeOffBackupApprover(timeOffBackupApprover);
        } catch (err) {
            this.notify.error(err);
        } finally {
            this.isLoading = false;
        }
    }
}
